import { makeArticle, saintRelated } from "./learnFactory";

export const pageHowDoesSaintWork = makeArticle({
  slug: "how-does-saint-tms-work",
  title: "How Does SAINT® TMS Work? | Neuro Wellness",
  description:
    "How SAINT® TMS works: fMRI-guided targeting, intermittent theta burst stimulation, 10 sessions per day for 5 days, and how it differs from conventional TMS.",
  canonical: "/learn/how-does-saint-tms-work",
  h1: "How Does SAINT® TMS Work?",
  breadcrumbLabel: "How SAINT® TMS Works",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) works by using a functional MRI scan to identify each patient's individualized brain stimulation target, then delivering intermittent theta burst stimulation (iTBS) — 10 short sessions per day for 5 consecutive days, with 50-minute breaks between sessions. This is different from conventional TMS, which uses scalp measurements and delivers one session per day over 4–6 weeks. SAINT® is FDA-cleared for adults with major depressive disorder who have not improved on antidepressant medication.",
  keyTakeaways: [
    "SAINT® uses fMRI to identify each patient's individualized stimulation target.",
    "Treatment uses intermittent theta burst stimulation (iTBS), a specific pulse pattern.",
    "10 sessions are delivered per day for 5 consecutive days, with 50-minute breaks.",
    "The accelerated schedule and personalized targeting distinguish SAINT® from conventional TMS.",
    "SAINT® is FDA-cleared for adults whose depression has not improved on antidepressants.",
  ],
  sections: [
    {
      heading: "The Three Components of SAINT®",
      paragraphs: [
        "SAINT® combines three elements that distinguish it from conventional TMS:",
      ],
      list: [
        "Functional MRI targeting — a brain scan identifies each patient's individualized stimulation site based on their own brain connectivity.",
        "Intermittent theta burst stimulation (iTBS) — a specific pulse pattern delivering bursts of 3 pulses at 50 Hz, about 1,800 pulses per 10-minute session.",
        "Accelerated schedule — 10 sessions per day for 5 consecutive days, with roughly 50-minute breaks between sessions, totaling 50 sessions in one week.",
      ],
    },
    {
      heading: "How fMRI Targeting Works",
      paragraphs: [
        "Before treatment, each patient undergoes a structural and functional MRI scan. The functional MRI maps how different brain regions connect and communicate. A proprietary algorithm then analyzes this connectivity data to identify the specific point on the left dorsolateral prefrontal cortex that is most connected to deeper mood-regulating circuits in that individual's brain.",
        "This is different from conventional TMS, which typically uses scalp measurements from the motor threshold location (often the '5.5 cm rule') to estimate where to place the coil. SAINT® replaces that population-average estimate with a target computed from the patient's own brain data.",
      ],
    },
    {
      heading: "Why the Accelerated Schedule Matters",
      paragraphs: [
        "Conventional TMS delivers one session per day, five days a week, for about 4–6 weeks. SAINT® delivers 10 sessions per day for 5 days. The 50-minute breaks between sessions are a deliberate part of the protocol — they allow the brain to respond to the stimulation pattern.",
        "In the Stanford research, this accelerated schedule with iTBS produced a faster and larger effect than once-daily stimulation. The 2022 double-blind, sham-controlled trial reported 78.6% remission in the active group versus 13.3% in the sham group at 4 weeks.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "SAINT® works through a combination of personalized brain targeting and an accelerated stimulation schedule. It is FDA-cleared for adults with major depressive disorder who have not improved on antidepressant medication. Because it requires an fMRI and the Magnus Neuromodulation System, it is available at fewer clinics than conventional TMS.",
        "Individual results vary. The clinical trials were small and conducted at a single site. A psychiatric evaluation determines whether SAINT® is appropriate for a specific person.",
      ],
      links: [
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/learn/saint-vs-traditional-tms",
          text: "Compare SAINT® to traditional TMS",
        },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "How does SAINT® TMS work?",
      answer:
        "SAINT® works by using a functional MRI to identify each patient's individualized brain stimulation target, then delivering intermittent theta burst stimulation — 10 sessions per day for 5 consecutive days with 50-minute breaks. The fMRI targeting and accelerated schedule distinguish it from conventional TMS.",
    },
    {
      question: "What is intermittent theta burst stimulation (iTBS)?",
      answer:
        "iTBS is a TMS pulse pattern that delivers bursts of 3 pulses at 50 Hz, repeated at 5 Hz. It delivers about 1,800 pulses in a 10-minute session. The THREE-D trial showed iTBS was non-inferior to conventional 10 Hz TMS when delivered once daily. SAINT® uses iTBS in an accelerated 10-session-per-day schedule.",
    },
    {
      question: "How is SAINT® different from regular TMS?",
      answer:
        "SAINT® uses fMRI to personalize the stimulation target, delivers 10 sessions per day for 5 days (50 total), and uses iTBS pulse patterns. Conventional TMS uses scalp measurements for targeting, delivers one session per day for 4–6 weeks (30–36 total), and typically uses 10 Hz stimulation. No trial has compared them head-to-head.",
    },
    {
      question: "Why does SAINT® use 10 sessions per day?",
      answer:
        "The accelerated schedule of 10 sessions per day with 50-minute breaks is part of the Stanford-developed protocol. In the research, this schedule with iTBS produced a faster and larger effect than once-daily stimulation. The breaks allow the brain to respond to the stimulation pattern between sessions.",
    },
    {
      question:
        "Does SAINT® target a different part of the brain than regular TMS?",
      answer:
        "SAINT® targets the same general region — the left dorsolateral prefrontal cortex — but uses fMRI to identify the specific point most connected to deeper mood circuits in each individual patient. Conventional TMS uses scalp measurements to estimate the target, which is a population average rather than a personalized location.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
  ],
  relatedLinks: saintRelated,
});
